import React, { useState, useEffect, FormEvent } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { StripeError } from "@stripe/stripe-js";
import { clearListSubscription } from "../../Redux/Slices/Subscription/ListSubscriptionSlice";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { Button } from "@mui/material";

// Replace with your actual publishable key

const stripePromise = loadStripe(`${process.env.REACT_APP_PAYMENT_KEY}`);

interface StripeCheckoutProps {
  currency: string;
  clientSecret: string | null;
  onSuccess?: () => void;
  onError?: (error: StripeError | Error) => void;
  onProcessingChange: React.Dispatch<React.SetStateAction<boolean>>; // 👈 NEW
}

const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  currency,
  clientSecret,
  onSuccess,
  onError,
  onProcessingChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onProcessingChange(true);

    if (!stripe || !elements || !clientSecret) return;

    setIsProcessing(true);

    const { error: submitError } = await elements.submit();

    if (submitError) {
      setErrorMessage(submitError?.message || null); // Use null as default
      setIsProcessing(false);
      onProcessingChange(false);  
      return;
    }

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          // Optionally include billing details here
          // billing_details: { ... }
        },
        redirect: "if_required",
      });

      if (confirmError) {
        setErrorMessage(
          confirmError.message || "An error occurred during payment."
        );
        console.error("[Payment error]", confirmError);

        const wrappedError = new Error(confirmError.message);
        onError?.(wrappedError);
      } else {
        console.log("Payment successful!");
        onSuccess?.();
      }
    } catch (error: unknown) {
      const typedError = error as Error;
      setErrorMessage(typedError.message);
      console.error("Error during payment:", typedError);
      onError?.(typedError);
    } finally {
      setIsProcessing(false);
      onProcessingChange(false);  
    }
  };
  const dispatch = useAppDispatch();

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMessage && <div style={{ color: "red" }}>{errorMessage}</div>}
      <Button
        type="submit"
        sx={{
          backgroundColor: "#009dff",
          color: "#fff",
          mt: 2, // equivalent to margin-top: 16px
          textTransform: "capitalize",
          padding: "10px",
          fontSize: "13px",
          width: "100%",
          "&:hover": {
            backgroundColor: "#009dff",
          },
        }}
        disabled={!stripe || isProcessing || !clientSecret}
      >
        {isProcessing ? "Processing..." : `Pay`}
      </Button>
    </form>
  );
};

interface StripeWrapperProps {
  paymentCurrency: string;
  onPaymentSuccess?: () => void;
  onPaymentError?: (error: StripeError | Error) => void;
  // onLoadingChange?: (loading: boolean) => void;
  onProcessingChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const StripeWrapper: React.FC<StripeWrapperProps> = ({
  paymentCurrency,
  onPaymentSuccess,
  onPaymentError,
  onProcessingChange,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  const { data: SubscriptionSlice } = useAppSelector(
    (state) => state.ListSubscriptionSlice
  );
  const Subcriptiondetails: any = SubscriptionSlice?.data;

  useEffect(() => {
    const createPaymentIntent = async () => {
      setIsLoading(true);

      try {
        // const response = await fetch("/create-payment-intent", {
        //   method: "POST",
        //   headers: { "Content-Type": "application/json" },
        //   body: JSON.stringify({
        //     // Include amount here if your backend expects it for PaymentIntent creation
        //     // amount: someAmount,
        //     currency: paymentCurrency,
        //   }),
        // });

        // if (!response.ok) {
        //   const errorData = await response.json();
        //   throw new Error(errorData.error || "Failed to create Payment Intent");
        // }

        // const responseData = await response.json();
        const fetchedClientSecret = Subcriptiondetails?.client_secret;

        if (!fetchedClientSecret) {
          throw new Error("Missing client_secret in response.");
        }

        setClientSecret(fetchedClientSecret);
      } catch (error: unknown) {
        const typedError = error as Error;
        console.error("Error creating Payment Intent:", typedError);
        setFetchError(typedError);
        onPaymentError?.(typedError);
      } finally {
        setIsLoading(false);
        // onProcessingChange?.(false); // 👈 Notify parent
      }
    };

    createPaymentIntent();
  }, [paymentCurrency, onPaymentError]); // Removed paymentAmount as it's not used here

  if (isLoading) {
    return <div>Loading payment...</div>;
  }

  if (fetchError) {
    return <div>Error loading payment: {fetchError.message}</div>;
  }

  return clientSecret ? (
    <div>
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <StripeCheckout
          currency={paymentCurrency}
          clientSecret={clientSecret}
          onSuccess={onPaymentSuccess}
          onError={onPaymentError}
          onProcessingChange={onProcessingChange} // 👈 NEW
        />
      </Elements>
    </div>
  ) : null; // Return null when clientSecret is not available and not loading/error
};

export default StripeWrapper;
